import { assertStaffCapability, assertStaffRelationship, } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { DomainValidationError } from "./identifiers.js";
import { hasBlockingIssue } from "./itinerary-validation.js";
import { weatherSensitivities } from "./itinerary-weather.js";
import { ianaTimeZone, localDate, localTimeOfDay } from "./time-zone.js";
import { checkExpectedVersion, denied, isMember, requireReason, violation, } from "./transition-support.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const itineraryStatuses = Object.freeze([
    "DRAFT",
    "ACTIVE",
    "SUPERSEDED",
    "ARCHIVED",
]);
export const itineraryItemKinds = Object.freeze([
    "CATALOG_ITEM",
    "EXTERNAL_PLACE",
    "TRANSIT",
    "BUFFER",
    "CUSTOM_ACTIVITY",
    "CONCIERGE_REQUEST",
]);
/** Never a booking state: a request or payment link on an item is not proof of booking. */
export const itineraryItemStatuses = Object.freeze(["PLANNED", "TENTATIVE", "CANCELLED"]);
export const itineraryTimingKinds = Object.freeze(["SCHEDULED", "ALL_DAY", "UNTIMED"]);
export const itinerarySectionKinds = Object.freeze(["DAY", "FLEXIBLE"]);
export const itineraryProvenances = Object.freeze([
    "CUSTOMER_ENTRY",
    "STAFF_ENTRY",
    "AI_SUGGESTION",
    "REVISION_COPY",
]);
/** Reserved for Phase 9 (routes): the columns exist and stay unwritten in Phase 2.4. */
export const routeModes = Object.freeze(["WALK", "DRIVE", "TRANSIT", "FLIGHT", "OTHER"]);
/** `UNKNOWN` until Phase 9 validates routes and opening hours: a generated plan is not known to be feasible. */
export const feasibilityStatuses = Object.freeze(["UNKNOWN", "FEASIBLE", "INFEASIBLE"]);
/**
 * Who confirmed the zone of a scheduled item (CHT-742, contract 3.4 and 4.2): the catalog item's own zone, or explicit IANA text
 * that staff or the customer confirmed. Never a zone a provider reported, and never a device zone. Frozen: the database CHECK is
 * generated from this tuple. A row written before Milestone D has no source (`null`).
 */
export const timeZoneSources = Object.freeze(["CATALOG", "STAFF", "CUSTOMER"]);
export const itineraryCommands = Object.freeze([
    "activate",
    "discard",
    "withdraw",
    "supersede",
]);
const rule = (id, from, to, command, customer) => Object.freeze({ id, from, to, command, customer, staff: true });
/**
 * The four allowed transitions. I3 (ACTIVE to SUPERSEDED) is never requested on its own: it is the
 * side effect an activation (I1) has on its base, in the same transaction. The database guard
 * (LF013) allows exactly these (from, to) pairs.
 */
export const itineraryTransitions = Object.freeze([
    rule("I1", "DRAFT", "ACTIVE", "activate", true),
    rule("I2", "DRAFT", "ARCHIVED", "discard", true),
    rule("I3", "ACTIVE", "SUPERSEDED", "supersede", false),
    rule("I4", "ACTIVE", "ARCHIVED", "withdraw", false),
]);
export function isAllowedItineraryTransition(from, to) {
    return itineraryTransitions.some((row) => row.from === from && row.to === to);
}
const isFinal = (status) => status === "SUPERSEDED" || status === "ARCHIVED";
/**
 * Who may act on an itinerary at all: a customer must own the trip and may only see the drafts a
 * customer created (another customer's, and a staff draft, are `not_owner`: nothing about them
 * leaks); staff need `trip.manage` and an effective assignment or the supervisor role.
 */
function assertItineraryActor(itinerary, actor, environment, { hiddenStaffDraft }) {
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== itinerary.customerId)
                throw denied("not_owner");
            if (hiddenStaffDraft && itinerary.status === "DRAFT" && itinerary.createdBy === "STAFF") {
                throw denied("not_owner");
            }
            return;
        case "staff":
            assertStaffCapability(actor, "trip.manage", environment);
            assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
            return;
        case "system":
            throw denied("role_missing");
    }
}
/**
 * Plans I1, I2 or I4, or refuses. Checks run in this order: (1) the actor's standing (a customer
 * must own the trip, staff need `trip.manage`), (2) the pair (`itinerary.not_mutable` from SUPERSEDED
 * or ARCHIVED, else `itinerary.transition_invalid`; I3 is never a command of its own), (3) the actor
 * against the rule (a customer only for I1 and I2, and only on a customer-created draft; staff need an
 * effective assignment or the supervisor role), (4) `expectedVersion`, (5) the staff reason (I4), then
 * (6) for I1 the evidence: the trip is open, the base is still the plan of record (or no plan
 * exists), and the content has no blocking issue. Nothing is written here.
 */
export function planItineraryTransition(input) {
    const { itinerary, to, command, actor, context } = input;
    // Standing only: the draft-visibility rule comes after the rule says whether a customer may act.
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== itinerary.customerId)
                throw denied("not_owner");
            break;
        case "staff":
            assertStaffCapability(actor, "trip.manage", input.environment);
            break;
        case "system":
            break;
    }
    if (isFinal(itinerary.status))
        throw violation("itinerary.not_mutable");
    const matched = itineraryTransitions.find((row) => row.from === itinerary.status && row.to === to);
    if (matched === undefined || matched.command !== command || command === "supersede") {
        throw violation("itinerary.transition_invalid");
    }
    switch (actor.kind) {
        case "customer":
            if (!matched.customer)
                throw denied("role_missing");
            if (itinerary.createdBy === "STAFF")
                throw denied("not_owner");
            break;
        case "system":
            throw denied("role_missing");
        case "staff":
            assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
            break;
    }
    // A customer makes a draft the plan of record only on the version they read (design I1): staff
    // may have added an item since, and an activation must not adopt a plan nobody has seen.
    if (command === "activate" && actor.kind === "customer" && input.expectedVersion === null) {
        throw new DomainValidationError("expectedVersion", "is required");
    }
    checkExpectedVersion(actor, input.expectedVersion, itinerary.version, "itinerary.version_conflict");
    if (command === "withdraw")
        requireReason(input.reason);
    if (command === "activate") {
        if (context.tripStatus === "COMPLETED" || context.tripStatus === "CANCELLED") {
            throw violation("itinerary.trip_closed");
        }
        if (itinerary.supersedesItineraryId !== null) {
            if (context.base === null || context.base.status !== "ACTIVE") {
                throw violation("itinerary.base_not_active");
            }
        }
        else if (context.activeItineraryId !== null) {
            throw violation("itinerary.active_exists");
        }
        if (hasBlockingIssue(context.issues))
            throw violation("itinerary.invalid_structure");
        return Object.freeze({
            ruleId: matched.id,
            command,
            from: itinerary.status,
            to,
            patch: {
                kind: "set_status",
                status: "ACTIVE",
                activatedAt: "now",
            },
            supersededBase: itinerary.supersedesItineraryId === null
                ? null
                : {
                    itineraryId: itinerary.supersedesItineraryId,
                    patch: {
                        kind: "set_status",
                        status: "SUPERSEDED",
                        supersededAt: "now",
                    },
                },
            auditAction: "itinerary.activated",
        });
    }
    return Object.freeze({
        ruleId: matched.id,
        command,
        from: itinerary.status,
        to,
        patch: { kind: "set_status", status: "ARCHIVED", archivedAt: "now" },
        supersededBase: null,
        auditAction: command === "withdraw" ? "itinerary.withdrawn" : "itinerary.discarded",
    });
}
/**
 * A content edit (a new section or item): only a DRAFT is writable, and every edit bumps the
 * version by one (the adapter writes it as a compare-and-set on `expectedVersion`). ACTIVE content
 * is immutable too: a change to the plan of record is a new revision.
 */
export function planItineraryContentEdit(input) {
    const { itinerary, actor } = input;
    assertItineraryActor(itinerary, actor, input.environment, { hiddenStaffDraft: true });
    if (itinerary.status !== "DRAFT")
        throw violation("itinerary.not_mutable");
    checkExpectedVersion(actor, input.expectedVersion, itinerary.version, "itinerary.version_conflict");
    return Object.freeze({ nextVersion: itinerary.version + 1 });
}
const fail = (field, rule) => new DomainValidationError(field, rule);
const present = (value) => value !== null && value !== undefined;
/**
 * A DAY section needs its local date and the zone that date is read in; a FLEXIBLE section (the
 * undated "ideas" group) has neither. Positions are assigned by the adapter, never by the caller.
 */
export function validateItinerarySectionInput(input) {
    if (!isMember(itinerarySectionKinds, input.kind))
        throw fail("kind", "must be a known section kind");
    const date = present(input.localDate) ? localDate(input.localDate, "localDate") : null;
    const zone = present(input.timeZone) ? ianaTimeZone(input.timeZone, "timeZone") : null;
    if (input.kind === "DAY") {
        if (date === null)
            throw fail("localDate", "is required for a day section");
        if (zone === null)
            throw fail("timeZone", "is required for a day section");
    }
    else if (date !== null || zone !== null) {
        throw fail("kind", "a flexible section has no date or time zone");
    }
    const title = present(input.title) ? boundedText("title", input.title, 140) : null;
    return Object.freeze({ kind: input.kind, localDate: date, timeZone: zone, title });
}
const PROVIDER = /^[a-z][a-z0-9_]*$/;
const COUNTRY = /^[A-Z]{2}$/;
const ENTERED_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
/** An entered wall time (`YYYY-MM-DDTHH:MM`) and its offset in minutes: both or neither, a real date and a real clock time. */
function enteredWallTime(field, local, offset) {
    const text = local ?? null;
    const minutes = offset ?? null;
    if ((text === null) !== (minutes === null)) {
        throw fail(field, "needs its UTC offset and the other way round");
    }
    if (text === null || minutes === null)
        return { local: null, offset: null };
    if (!ENTERED_LOCAL.test(text))
        throw fail(field, "must be YYYY-MM-DDTHH:MM");
    localDate(text.slice(0, 10), field);
    localTimeOfDay(text.slice(11), field);
    if (!Number.isInteger(minutes) || minutes < -1440 || minutes > 1440) {
        throw fail(field, "needs a UTC offset in whole minutes within a day");
    }
    return { local: text, offset: minutes };
}
const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
function externalPlace(value) {
    if (typeof value.provider !== "string" || !PROVIDER.test(value.provider)) {
        throw fail("externalPlace.provider", "must be a lowercase provider code");
    }
    if (!isValidDate(value.observedAt))
        throw fail("externalPlace.observedAt", "must be a valid date");
    return {
        provider: value.provider,
        id: boundedText("externalPlace.id", value.id, 255),
        name: boundedText("externalPlace.name", value.name, 200),
        observedAt: value.observedAt,
    };
}
/**
 * Validates one item, mirroring the database CHECKs: each kind carries exactly its references (a
 * catalog item; an external place with its provider and observation time, which never becomes a
 * catalog item on its own; a concierge request, which also may ride on a catalog, place or custom
 * item but never on transit or a buffer), a SCHEDULED item has a start and a zone, the end is not
 * before the start and excludes a duration, and the reserved route fields are refused. The
 * customer note and the staff note are separate fields; only the first is customer-visible.
 */
export function validateItineraryItemInput(input) {
    if (!isMember(itineraryItemKinds, input.kind))
        throw fail("kind", "must be a known item kind");
    if (!isMember(itineraryTimingKinds, input.timingKind)) {
        throw fail("timingKind", "must be a known timing kind");
    }
    if (!isMember(itineraryProvenances, input.provenance)) {
        throw fail("provenance", "must be a known provenance");
    }
    const itemStatus = input.itemStatus ?? "PLANNED";
    if (!isMember(itineraryItemStatuses, itemStatus))
        throw fail("itemStatus", "must be a known status");
    if (present(input.routeFromPreviousMode) ||
        present(input.routeFromPreviousMinutes) ||
        present(input.routeComputedAt)) {
        throw fail("route", "is reserved");
    }
    const catalogItemId = input.catalogItemId ?? null;
    const place = present(input.externalPlace) ? externalPlace(input.externalPlace) : null;
    const requestId = input.conciergeRequestId ?? null;
    if ((input.kind === "CATALOG_ITEM") !== (catalogItemId !== null)) {
        throw fail("catalogItemId", "is required for a catalog item and only then");
    }
    if ((input.kind === "EXTERNAL_PLACE") !== (place !== null)) {
        throw fail("externalPlace", "is required for an external place and only then");
    }
    if (input.kind === "CONCIERGE_REQUEST" && requestId === null) {
        throw fail("conciergeRequestId", "is required for a request item");
    }
    if ((input.kind === "TRANSIT" || input.kind === "BUFFER") && requestId !== null) {
        throw fail("conciergeRequestId", "is not allowed on transit or a buffer");
    }
    const zone = present(input.timeZone) ? ianaTimeZone(input.timeZone, "timeZone") : null;
    const startsAt = input.startsAt ?? null;
    const endsAt = input.endsAt ?? null;
    const duration = input.durationMinutes ?? null;
    if (startsAt !== null && !isValidDate(startsAt))
        throw fail("startsAt", "must be a valid date");
    if (endsAt !== null && !isValidDate(endsAt))
        throw fail("endsAt", "must be a valid date");
    if (input.timingKind === "SCHEDULED") {
        if (startsAt === null)
            throw fail("startsAt", "is required for a scheduled item");
        if (zone === null)
            throw fail("timeZone", "is required for a scheduled item");
    }
    else if (startsAt !== null || endsAt !== null) {
        throw fail("startsAt", "is only for a scheduled item");
    }
    if (startsAt !== null && endsAt !== null && endsAt.getTime() < startsAt.getTime()) {
        throw fail("endsAt", "must not be before the start");
    }
    if (endsAt !== null && duration !== null) {
        throw fail("durationMinutes", "cannot be given together with an end");
    }
    if (duration !== null && (!Number.isInteger(duration) || duration < 1 || duration > 10_080)) {
        throw fail("durationMinutes", "must be a whole number from 1 to 10080");
    }
    const latitude = input.latitude ?? null;
    const longitude = input.longitude ?? null;
    if ((latitude === null) !== (longitude === null)) {
        throw fail("latitude", "needs a longitude and the other way round");
    }
    if (latitude !== null && longitude !== null) {
        if (typeof latitude !== "number" || !(latitude >= -90 && latitude <= 90)) {
            throw fail("latitude", "must be from -90 to 90");
        }
        if (typeof longitude !== "number" || !(longitude >= -180 && longitude <= 180)) {
            throw fail("longitude", "must be from -180 to 180");
        }
    }
    const countryCode = input.countryCode ?? null;
    if (countryCode !== null && !COUNTRY.test(countryCode)) {
        throw fail("countryCode", "must be a two-letter uppercase country code");
    }
    const timeZoneSource = input.timeZoneSource ?? null;
    if (timeZoneSource !== null && !isMember(timeZoneSources, timeZoneSource)) {
        throw fail("timeZoneSource", "must be CATALOG, STAFF or CUSTOMER");
    }
    if (timeZoneSource !== null && zone === null) {
        throw fail("timeZoneSource", "needs a time zone");
    }
    const timeZoneObservedAt = input.timeZoneObservedAt ?? null;
    if (timeZoneObservedAt !== null && !isValidDate(timeZoneObservedAt)) {
        throw fail("timeZoneObservedAt", "must be a valid date");
    }
    const enteredStart = enteredWallTime("enteredLocalStart", input.enteredLocalStart, input.enteredUtcOffsetMinutes);
    if (enteredStart.local !== null && input.timingKind !== "SCHEDULED") {
        throw fail("enteredLocalStart", "is only for a scheduled item");
    }
    const endTimeZone = present(input.endTimeZone)
        ? ianaTimeZone(input.endTimeZone, "endTimeZone")
        : null;
    const enteredEnd = enteredWallTime("enteredLocalEnd", input.enteredLocalEnd, input.enteredEndUtcOffsetMinutes);
    if ((endTimeZone !== null || enteredEnd.local !== null) && endsAt === null) {
        throw fail("endTimeZone", "needs an end");
    }
    if (typeof (input.isFixed ?? false) !== "boolean")
        throw fail("isFixed", "must be true or false");
    const weatherSensitivity = input.weatherSensitivity ?? "NONE";
    if (!isMember(weatherSensitivities, weatherSensitivity)) {
        throw fail("weatherSensitivity", "must be NONE, OUTDOOR, WATER or HEIGHT");
    }
    return Object.freeze({
        kind: input.kind,
        itemStatus,
        title: boundedText("title", input.title, 200),
        timingKind: input.timingKind,
        startsAt,
        endsAt,
        timeZone: zone,
        durationMinutes: duration,
        locationName: present(input.locationName)
            ? boundedText("locationName", input.locationName, 200)
            : null,
        locality: present(input.locality) ? boundedText("locality", input.locality, 100) : null,
        countryCode,
        latitude,
        longitude,
        catalogItemId,
        externalPlace: place,
        conciergeRequestId: requestId,
        customerNote: present(input.customerNote)
            ? boundedText("customerNote", input.customerNote, 1000, { multiline: true })
            : null,
        staffNote: present(input.staffNote)
            ? boundedText("staffNote", input.staffNote, 1000, { multiline: true })
            : null,
        provenance: input.provenance,
        isFixed: input.isFixed ?? false,
        timeZoneSource,
        timeZoneObservedAt,
        enteredLocalStart: enteredStart.local,
        enteredUtcOffsetMinutes: enteredStart.offset,
        endTimeZone,
        enteredLocalEnd: enteredEnd.local,
        enteredEndUtcOffsetMinutes: enteredEnd.offset,
        weatherSensitivity,
    });
}
/**
 * Plans a new DRAFT revision of the plan of record: the base must be ACTIVE (a revision never starts
 * from a draft, a superseded or an archived plan). The copy has `revision = base.revision + 1`, links
 * the base through `supersedesItineraryId`, and keeps the origin ids so history reads item by item.
 * Activating the draft later supersedes the base (I1 with I3).
 */
export function planItineraryRevision(input) {
    const { base, actor } = input;
    assertItineraryActor(base.itinerary, actor, input.environment, { hiddenStaffDraft: false });
    if (base.itinerary.status !== "ACTIVE")
        throw violation("itinerary.base_not_active");
    checkExpectedVersion(actor, input.expectedVersion, base.itinerary.version, "itinerary.version_conflict");
    return Object.freeze({
        revision: base.itinerary.revision + 1,
        supersedesItineraryId: base.itinerary.id,
        createdBy: actor.kind === "staff" ? "STAFF" : "CUSTOMER",
        sections: base.sections.map((section) => ({
            originSectionId: section.id,
            position: section.position,
            kind: section.kind,
            localDate: section.localDate,
            timeZone: section.timeZone,
            title: section.title,
        })),
        items: base.items.map((item) => ({
            originItemId: item.id,
            lineageId: item.lineageId ?? item.id,
            originSectionId: item.sectionId,
            position: item.position,
            data: item.data,
            provenance: "REVISION_COPY",
        })),
    });
}
